// Implements: F-UIX-019, F-UIX-007, F-GEN-036
/**
 * The model settings and their history through the shared query cache.
 *
 * A save or a restore answers with the stored settings, which replace the cached ones, and the
 * history is read again because it has a new row. Both writes are mutations with `retry: false`
 * (and the client sends them once): a paid-cost setting is never repeated automatically.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import type {
  EstimateRequest,
  EstimateView,
  ModelSettingsRequest,
  ModelSettingsView,
  RestoreEnvRequest,
  RevisionsView,
} from "../../../types/modelSettings";
import { estimateModelSettings, fetchModelSettings, fetchRevisions, restoreEnvSettings, saveModelSettings } from "../api/modelSettingsApi";

export const MODEL_SETTINGS_KEY = ["admin", "model-settings"] as const;
export const REVISIONS_KEY = ["admin", "model-settings", "revisions"] as const;

export function useModelSettings(): UseQueryResult<ModelSettingsView, ApiError> {
  return useQuery<ModelSettingsView, ApiError>({
    queryKey: MODEL_SETTINGS_KEY,
    queryFn: ({ signal }) => fetchModelSettings(signal),
  });
}

/** Reads the settings again for a `409`: a direct fetch whose failure is `undefined`, never stale data. */
export function useReloadModelSettings(): () => Promise<ModelSettingsView | undefined> {
  const client = useQueryClient();
  return () =>
    fetchModelSettings().then(
      (view) => {
        client.setQueryData(MODEL_SETTINGS_KEY, view);
        return view;
      },
      () => undefined,
    );
}

export function useRevisions(): UseQueryResult<RevisionsView, ApiError> {
  return useQuery<RevisionsView, ApiError>({
    queryKey: REVISIONS_KEY,
    queryFn: ({ signal }) => fetchRevisions(signal),
  });
}

function useStoring<TRequest>(
  write: (request: TRequest) => Promise<ModelSettingsView>,
): UseMutationResult<ModelSettingsView, ApiError, TRequest> {
  const client = useQueryClient();
  return useMutation<ModelSettingsView, ApiError, TRequest>({
    mutationFn: write,
    retry: false,
    onSuccess: (stored) => {
      client.setQueryData(MODEL_SETTINGS_KEY, stored);
      void client.invalidateQueries({ queryKey: REVISIONS_KEY });
    },
  });
}

export function useSaveModelSettings(): UseMutationResult<ModelSettingsView, ApiError, ModelSettingsRequest> {
  return useStoring(saveModelSettings);
}

export function useRestoreEnv(): UseMutationResult<ModelSettingsView, ApiError, RestoreEnvRequest> {
  return useStoring(restoreEnvSettings);
}

/** The cost preview of a draft: a mutation (a POST read), never cached and never retried. */
export function useEstimate(): UseMutationResult<EstimateView, ApiError, EstimateRequest> {
  return useMutation<EstimateView, ApiError, EstimateRequest>({ mutationFn: estimateModelSettings, retry: false });
}
